/**
 * "Test assigned" notification poller.
 *
 * Short-polls StudentPortalApi
 *   GET {STUDENT_PORTAL_BASE_URL}notifications/tests/poll
 * every 10s and broadcasts the student's **latest** assigned test (a mentor assigning
 * a diagnostic test while they're in a live class) to all subscribed listeners.
 *
 * Two ways to subscribe (public API):
 *   - onTestAssignment(fn)         → listen only; does NOT start polling.
 *   - openTestAssignmentStream(fn) → listen AND keep polling alive while subscribed
 *                                    (ref-counted). The ongoing class card opens it
 *                                    only while a class is happening now.
 *
 * The poll endpoint returns the CURRENT set of non-expired assignments each time (a
 * per-student Redis cache with a 20-min TTL, class-ended payloads dropped server-side),
 * and there can be more than one. We track a single "current latest" (newest
 * `assignedAt`) and broadcast only when it changes — so a recurring test never re-fires,
 * and a newly-assigned one supersedes the previous. New subscribers are replayed the
 * current latest immediately (polling is state, not a one-shot event stream).
 *
 * Auth (Authorization / refresh-token / auth-source), 401 → token-refresh → retry, and
 * session-expiry are all handled by the shared axios interceptor in createApiClient.js,
 * so this module just calls API_STUDENT_PORTAL.get and ignores transient failures (the
 * next tick retries).
 *
 * Poll payload shape (written by EpicenterApi, returned verbatim by the endpoint):
 *   { event: "test_assigned", studentIds, configId, type, subject, grade,
 *     topicIds, assignedBy, assignedAt, expiresAt }
 */

import { API_STUDENT_PORTAL } from "./api-service";

const POLL_URL = "/notifications/tests/poll";
const POLL_MS = 10000;

// Every consumer (card indicator + app-root popup) is broadcast to.
const listeners = new Set();
// Ref-count of consumers that want polling held alive (see openTestAssignmentStream).
let openRefs = 0;
// setInterval handle while polling is active/visible; null when paused or stopped.
let intervalId = null;
// In-flight guard so only one poll request is ever outstanding.
let isPolling = false;
// The latest assigned-test payload currently surfaced (or null). Broadcast only when
// its configId changes; replayed to new subscribers. Reset on stop.
let current = null;
// Bumped on stop so a poll request that resolves after the poller stopped is discarded.
let pollGen = 0;

const broadcast = (payload) => {
  listeners.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      // One bad listener must not break the others.
    }
  });
};

// Epoch ms of a payload's assign time (assignedAt, falling back to createdAt); 0 when
// absent so entries without a timestamp never beat a real one.
const assignedTime = (test) => {
  const t = Date.parse(test?.assignedAt ?? test?.createdAt ?? "");
  return Number.isNaN(t) ? 0 : t;
};

const poll = async () => {
  if (isPolling) return; // only one request in flight at a time
  isPolling = true;
  const gen = pollGen;
  try {
    const res = await API_STUDENT_PORTAL.get(POLL_URL);
    if (gen !== pollGen) return; // poller was stopped while this request was in flight
    const tests = res?.data?.tests;
    // Pick the latest of possibly-many pending tests.
    const latest =
      Array.isArray(tests) && tests.length
        ? tests.reduce((a, b) => (assignedTime(b) > assignedTime(a) ? b : a))
        : null;
    const latestConfigId = latest?.configId ?? null;
    if (latestConfigId !== (current?.configId ?? null)) {
      current = latestConfigId != null ? latest : null;
      if (current) broadcast(current); // payload already { event: "test_assigned", configId, ... }
    }
  } catch (err) {
    // Auth/refresh/retry + session-expiry are handled by the axios interceptor;
    // a transient failure here is ignored and the next tick retries.
  } finally {
    isPolling = false;
  }
};

const startInterval = () => {
  if (!intervalId) intervalId = setInterval(poll, POLL_MS);
};

const clearIntervalIfAny = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

// Pause polling while the tab is hidden; on becoming visible again poll immediately
// and resume the interval. (Tab visibility only — NOT window focus: during a live
// class the student is often focused on the Zoom window, so the tab stays visible
// but blurred, which is exactly when a test gets assigned.)
const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    poll();
    startInterval();
  } else {
    clearIntervalIfAny();
  }
};

const startPolling = () => {
  current = null;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  if (document.visibilityState === "visible") {
    poll();
    startInterval();
  }
  // If hidden at start, wait for visibilitychange → visible.
};

const stopPolling = () => {
  clearIntervalIfAny();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  current = null;
  isPolling = false;
  pollGen += 1; // invalidate any in-flight poll
};

// Deliver the current latest to a just-added listener, so a subscriber that joins after
// the latest was already seen still gets it.
const replayCurrent = (listener) => {
  if (!current) return;
  try {
    listener(current);
  } catch (err) {
    // ignore a bad listener
  }
};

/**
 * Listen for events WITHOUT starting the poller — for consumers that should render
 * events but not keep polling alive (the app-root popup).
 * @param {(payload: object) => void} listener
 * @returns {() => void} unsubscribe
 */
export const onTestAssignment = (listener) => {
  listeners.add(listener);
  replayCurrent(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Listen for events AND keep polling alive while subscribed. Ref-counted — polling
 * starts on the first opener and stops after the last one unsubscribes. Used by the
 * ongoing class card, so polling runs only while a class is happening now.
 * @param {(payload: object) => void} listener
 * @returns {() => void} unsubscribe (also releases the poller)
 */
export const openTestAssignmentStream = (listener) => {
  listeners.add(listener);
  openRefs += 1;
  if (openRefs === 1) startPolling();
  replayCurrent(listener);
  return () => {
    listeners.delete(listener);
    openRefs -= 1;
    if (openRefs <= 0) {
      openRefs = 0;
      stopPolling();
    }
  };
};
